import { useState, useEffect } from 'react';
import {
  Send, CheckCircle2, Eye, XCircle,
  Star, Loader2, RefreshCw, TrendingUp, Gauge, Infinity
} from 'lucide-react';
import { API_BASE_URL, WabaDispatchUsage } from '../../services/api';
import { getQualityRatingColor, getQualityRatingLabel } from '../../utils/statusColors';
import type { StatisticsSummary, QualityHistoryResponse } from '../../types/statistics';

interface SimpleDashboardProps {
  wabaAccountId: string;
}

/**
 * Dashboard simplificado para usuários comuns
 * Exibe apenas métricas básicas de envio (sem custos)
 */
export default function SimpleDashboard({ wabaAccountId }: SimpleDashboardProps) {
  // Data states
  const [summary, setSummary] = useState<StatisticsSummary | null>(null);
  const [qualityInfo, setQualityInfo] = useState<QualityHistoryResponse | null>(null);
  const [dispatchUsage, setDispatchUsage] = useState<WabaDispatchUsage | null>(null);

  // Loading states
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingQuality, setLoadingQuality] = useState(false);
  const [loadingDispatchUsage, setLoadingDispatchUsage] = useState(false);

  // Error states
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [qualityError, setQualityError] = useState<string | null>(null);
  const [dispatchUsageError, setDispatchUsageError] = useState<string | null>(null);

  // Fetch data on mount and when wabaAccountId changes
  useEffect(() => {
    fetchAllData();
  }, [wabaAccountId]);

  const fetchAllData = () => {
    fetchSummary();
    fetchQualityInfo();
    fetchDispatchUsage();
  };

  const fetchSummary = async () => {
    setLoadingSummary(true);
    setSummaryError(null);

    try {
      const token = localStorage.getItem('accessToken');

      // Últimos 30 dias
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);

      const params = new URLSearchParams({
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      });

      const response = await fetch(
        `${API_BASE_URL}/api/waba/${wabaAccountId}/statistics/user-summary?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao buscar estatísticas');
      }

      const data = await response.json();

      // Mapear campos da API para o formato esperado
      const apiStats = data.data?.statistics || data.statistics || {};
      const mappedSummary: StatisticsSummary = {
        totalMessages: apiStats.totalMessagesSent || 0,
        sentMessages: apiStats.totalMessagesSent || 0,
        deliveredMessages: apiStats.messagesDelivered || 0,
        readMessages: apiStats.messagesRead || 0,
        failedMessages: apiStats.messagesFailed || 0,
        deliveryRate: apiStats.deliveryRate || 0,
        readRate: apiStats.readRate || 0,
        totalCost: 0, // Não exibimos custo para usuários
        averageCostPerMessage: 0,
        period: {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          periodType: 'daily'
        }
      };

      setSummary(mappedSummary);
    } catch (err) {
      setSummaryError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoadingSummary(false);
    }
  };

  const fetchQualityInfo = async () => {
    setLoadingQuality(true);
    setQualityError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/api/waba/${wabaAccountId}/quality-history`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao buscar qualidade');
      }

      const data = await response.json();
      setQualityInfo(data.data || data);
    } catch (err) {
      setQualityError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoadingQuality(false);
    }
  };

  const fetchDispatchUsage = async () => {
    setLoadingDispatchUsage(true);
    setDispatchUsageError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/api/waba/${wabaAccountId}/dispatch-usage`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao buscar uso de disparos');
      }

      const data = await response.json();
      setDispatchUsage(data.data || data);
    } catch (err) {
      setDispatchUsageError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoadingDispatchUsage(false);
    }
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const isLoading = loadingSummary || loadingQuality || loadingDispatchUsage;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-1">Resumo de Envios</h2>
          <p className="text-sm text-text-3 mt-1">
            Estatísticas dos últimos 30 dias
          </p>
        </div>

        {/* Refresh button */}
        <button
          onClick={fetchAllData}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          <span className="text-sm">Atualizar</span>
        </button>
      </div>

      {/* Summary Cards - 4 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Messages */}
        <div className="bg-bg-2 p-6 rounded-lg border border-border-1">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-info/10 rounded-lg">
              <Send size={20} className="text-accent-1" />
            </div>
            {loadingSummary && <Loader2 size={16} className="animate-spin text-text-3" />}
          </div>
          <h3 className="text-sm text-text-3 mb-1">Mensagens Enviadas</h3>
          {summaryError ? (
            <p className="text-sm text-danger">{summaryError}</p>
          ) : (
            <p className="text-2xl font-bold text-text-1">
              {summary ? formatNumber(summary.sentMessages) : '-'}
            </p>
          )}
        </div>

        {/* Delivered Messages */}
        <div className="bg-bg-2 p-6 rounded-lg border border-border-1">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-success/10 rounded-lg">
              <CheckCircle2 size={20} className="text-success" />
            </div>
            {loadingSummary && <Loader2 size={16} className="animate-spin text-text-3" />}
          </div>
          <h3 className="text-sm text-text-3 mb-1">Entregues</h3>
          {summaryError ? (
            <p className="text-sm text-danger">{summaryError}</p>
          ) : (
            <>
              <p className="text-2xl font-bold text-text-1">
                {summary ? formatNumber(summary.deliveredMessages) : '-'}
              </p>
              {summary && summary.deliveryRate > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp size={14} className="text-success" />
                  <p className="text-xs text-success font-medium">
                    {formatPercentage(summary.deliveryRate)} taxa
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Read Messages */}
        <div className="bg-bg-2 p-6 rounded-lg border border-border-1">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-accent-2/10 rounded-lg">
              <Eye size={20} className="text-accent-2" />
            </div>
            {loadingSummary && <Loader2 size={16} className="animate-spin text-text-3" />}
          </div>
          <h3 className="text-sm text-text-3 mb-1">Lidas</h3>
          {summaryError ? (
            <p className="text-sm text-danger">{summaryError}</p>
          ) : (
            <>
              <p className="text-2xl font-bold text-text-1">
                {summary ? formatNumber(summary.readMessages) : '-'}
              </p>
              {summary && summary.readRate > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp size={14} className="text-accent-2" />
                  <p className="text-xs text-accent-2 font-medium">
                    {formatPercentage(summary.readRate)} taxa
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Failed Messages */}
        <div className="bg-bg-2 p-6 rounded-lg border border-border-1">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-danger/10 rounded-lg">
              <XCircle size={20} className="text-danger" />
            </div>
            {loadingSummary && <Loader2 size={16} className="animate-spin text-text-3" />}
          </div>
          <h3 className="text-sm text-text-3 mb-1">Falhas</h3>
          {summaryError ? (
            <p className="text-sm text-danger">{summaryError}</p>
          ) : (
            <>
              <p className="text-2xl font-bold text-text-1">
                {summary ? formatNumber(summary.failedMessages) : '-'}
              </p>
              {summary && summary.sentMessages > 0 && (
                <p className="text-xs text-text-3 mt-1">
                  {formatPercentage((summary.failedMessages / summary.sentMessages) * 100)}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Quality and Dispatch Limit Cards - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Quality Card */}
        <div className="bg-bg-2 p-6 rounded-lg border border-border-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-warning/10 rounded-lg">
                <Star size={24} className="text-warning" />
              </div>
              <div>
                <h3 className="text-sm text-text-3 mb-1">Qualidade da Conta</h3>
                {loadingQuality ? (
                  <Loader2 size={20} className="animate-spin text-text-3" />
                ) : qualityError ? (
                  <p className="text-sm text-danger">{qualityError}</p>
                ) : qualityInfo ? (
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getQualityRatingColor(qualityInfo.currentQualityRating)}`}>
                      {getQualityRatingLabel(qualityInfo.currentQualityRating)}
                    </span>
                    {qualityInfo.currentMessagingLimit && (
                      <span className="text-sm text-text-2">
                        Limite: {qualityInfo.currentMessagingLimit.replace('TIER_', '').replace('K', 'k')}/dia
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-text-3">Informação não disponível</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dispatch Limit Card */}
        <div className="bg-bg-2 p-6 rounded-lg border border-border-1">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-info/10 rounded-lg">
              <Gauge size={24} className="text-accent-1" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm text-text-3 mb-1">Limite de Disparo da Conta (24h)</h3>
              {loadingDispatchUsage ? (
                <Loader2 size={20} className="animate-spin text-text-3" />
              ) : dispatchUsageError ? (
                <p className="text-sm text-danger">{dispatchUsageError}</p>
              ) : dispatchUsage ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    {dispatchUsage.isUnlimited ? (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-success/10 text-success flex items-center gap-1">
                        <Infinity size={14} />
                        Ilimitado
                      </span>
                    ) : (
                      <>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          dispatchUsage.utilizationPercentage > 90
                            ? 'bg-danger/10 text-danger'
                            : dispatchUsage.utilizationPercentage > 70
                            ? 'bg-warning/10 text-warning'
                            : 'bg-success/10 text-success'
                        }`}>
                          {formatNumber(dispatchUsage.remainingIn24h)} restantes
                        </span>
                        <span className="text-sm text-text-2">
                          de {formatNumber(dispatchUsage.dailyDispatchLimit)}/dia
                        </span>
                      </>
                    )}
                  </div>
                  {!dispatchUsage.isUnlimited && (
                    <div className="w-full">
                      <div className="h-2 bg-bg-3 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all rounded-full ${
                            dispatchUsage.utilizationPercentage > 90
                              ? 'bg-danger'
                              : dispatchUsage.utilizationPercentage > 70
                              ? 'bg-warning'
                              : 'bg-success'
                          }`}
                          style={{ width: `${Math.min(100, dispatchUsage.utilizationPercentage)}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-text-3">
                          {formatNumber(dispatchUsage.usedIn24h)} usado
                        </span>
                        <span className="text-xs text-text-3">
                          {formatPercentage(dispatchUsage.utilizationPercentage)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-text-3">Informação não disponível</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
